import { useCallback, useMemo } from "react"

import { useAIExplorerSettings } from "@/hooks/use-module-settings"
import { useUserSettings } from "@/hooks/use-user-settings"

export function useProjectFavorites(sessionType: string) {
  const { settings, updateSettings, isLoading } = useAIExplorerSettings(sessionType)
  const { error: userSettingsError } = useUserSettings()
  const canToggleFavorites = !isLoading && !userSettingsError

  const favoriteProjectIds = useMemo(
    () => Array.from(new Set((settings?.favorite_project_ids ?? []).map((id) => String(id)))),
    [settings?.favorite_project_ids]
  )
  const favoriteProjectIdsSet = useMemo(() => new Set(favoriteProjectIds), [favoriteProjectIds])

  const isFavorited = useCallback(
    (projectId: string) => favoriteProjectIdsSet.has(projectId),
    [favoriteProjectIdsSet]
  )

  const toggleFavoriteProject = useCallback(
    (projectId: string) => {
      if (!canToggleFavorites) {
        return
      }

      const isAlreadyFavorite = favoriteProjectIdsSet.has(projectId)
      const nextFavoriteIds = isAlreadyFavorite
        ? favoriteProjectIds.filter((id) => id !== projectId)
        : [...favoriteProjectIds, projectId]

      updateSettings({
        favorite_project_ids: nextFavoriteIds,
      })
    },
    [canToggleFavorites, favoriteProjectIds, favoriteProjectIdsSet, updateSettings]
  )

  return {
    favoriteProjectIds,
    isFavorited,
    toggleFavoriteProject,
    canToggleFavorites,
    isLoading,
  }
}
